/**
 * Fret-diatonic recognition — guitar rung 6.
 *
 * A I–IV–V–I cadence establishes the key; then a diatonic chord (I, ii,
 * IV, V, or vi) plays. The CAGED shape region is shown with the non-root
 * chord tones as `given`; the user taps the root on the canonical string.
 *
 * The new cognitive demand over rung 5 (CAGED anchoring): the target chord
 * is not always the tonic, and the full key context is required to orient
 * the root position. The degree label ("Tap the IV root") is shown up front
 * so the ear-training task is resolving key context, not identifying the
 * Roman numeral.
 *
 * See docs/design/fret-diatonic-triads.md.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng, type Exercise, type FretboardPrompt } from "./exercise.js";
import { appendCadence, buildChord, type CadenceOptions } from "./utils.js";
import {
  STANDARD_TUNING,
  CAGED_SHAPES,
  midiAt,
  type FretPosition,
  type CagedShape,
  type CagedVoice,
} from "@music-app/theory-engine";
import { resolveVoiceMidi } from "./exercise-fret-caged.js";

// ─── Degree definitions ────────────────────────────────────────────────────────

export type DiatonicDegree = "I" | "ii" | "IV" | "V" | "vi";

/** Semitones above the major-key tonic for each diatonic degree. */
const DEGREE_SEMITONES: Record<DiatonicDegree, number> = {
  I: 0, ii: 2, IV: 5, V: 7, vi: 9,
};

/** Triad quality for each diatonic degree in major. */
const DEGREE_QUALITY: Record<DiatonicDegree, "maj" | "min"> = {
  I: "maj", ii: "min", IV: "maj", V: "maj", vi: "min",
};

// ─── Param type ───────────────────────────────────────────────────────────────

export interface FretDiatonicParams {
  shape: CagedShape;
  degree: DiatonicDegree;
}

// ─── Key helpers ──────────────────────────────────────────────────────────────

const PC_NAMES = ["C","D♭","D","E♭","E","F","F♯","G","A♭","A","B♭","B"] as const;

interface KeyChoice {
  name: string;
  tonicMidi: number;
  mode: "ionian";
  letter: string;
  accidental: -1 | 0 | 1;
}

function buildMajorKey(tonicMidi: number): KeyChoice {
  const pc = ((tonicMidi % 12) + 12) % 12;
  const pcName = PC_NAMES[pc]!;
  const hasFlat  = pcName.includes("♭");
  const hasSharp = pcName.includes("♯");
  const letter   = pcName.replace(/[♭♯]/u, "");
  const accidental: -1 | 0 | 1 = hasFlat ? -1 : hasSharp ? 1 : 0;
  const accSym = hasFlat ? "♭" : hasSharp ? "♯" : "";
  return {
    letter,
    accidental,
    mode: "ionian",
    tonicMidi: 60 + pc, // normalise to middle octave for cadence
    name: `${letter}${accSym} major`,
  };
}

// ─── Public generator ─────────────────────────────────────────────────────────

export function generateFretDiatonic(
  itemId: string,
  params: FretDiatonicParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);

  const shapeDef = CAGED_SHAPES[params.shape];
  if (!shapeDef) {
    throw new Error(`CAGED shape not implemented: ${params.shape}`);
  }

  const { canonicalRootString, pattern } = shapeDef;
  const minRootFret = shapeDef.minRootFret ?? 2;
  const quality = DEGREE_QUALITY[params.degree];
  const semitones = DEGREE_SEMITONES[params.degree];

  // Root fret for the DIATONIC CHORD (not the tonic) in [minRootFret, 12].
  const rootFret = minRootFret + rng.int(12 - minRootFret + 1);
  const diatonicRootMidi = midiAt(STANDARD_TUNING, { stringIndex: canonicalRootString, fret: rootFret });

  // Back-compute the tonic so the cadence establishes the correct key.
  const tonicMidi = diatonicRootMidi - semitones;
  const key = buildMajorKey(tonicMidi);

  // Chord audio shifted up one octave for clear mid-range sound.
  const chordAudioRoot = diatonicRootMidi + 12;
  const chordMidis = buildChord(chordAudioRoot, quality);
  const [chordRoot, chordThird, chordFifth] = chordMidis as [number, number, number];

  // First pass: resolve all voice positions from the diatonic chord root.
  interface VoicePos { stringIndex: number; fret: number; voice: CagedVoice; }
  const voicePositions: VoicePos[] = [];
  for (const [stringIndex, voice] of pattern) {
    const targetMidi = resolveVoiceMidi(diatonicRootMidi, voice, quality);
    const openMidi = midiAt(STANDARD_TUNING, { stringIndex, fret: 0 });
    const fret = targetMidi - openMidi;
    if (fret < 0 || fret > STANDARD_TUNING.fretCount) continue;
    voicePositions.push({ stringIndex, fret, voice });
  }

  // Dynamic fret window: shape extent ±1 fret.
  let minFret = rootFret;
  let maxFret = rootFret;
  for (const vp of voicePositions) {
    minFret = Math.min(minFret, vp.fret);
    maxFret = Math.max(maxFret, vp.fret);
  }
  const fretWindow = {
    from: Math.max(0, minFret - 1),
    to: Math.min(STANDARD_TUNING.fretCount, maxFret + 1),
  };

  // Second pass: partition voices.
  const given: FretPosition[] = [];
  const acceptedPositions: FretPosition[] = [];
  const solution: FretPosition[] = [];

  for (const { stringIndex, fret, voice } of voicePositions) {
    const pos: FretPosition = { stringIndex, fret };
    solution.push(pos);
    if (voice.role === "root" && stringIndex === canonicalRootString) {
      acceptedPositions.push(pos);
    } else if (voice.role !== "root") {
      given.push(pos);
    }
  }

  const fretboard: FretboardPrompt = {
    tuning: "standard",
    given,
    allowedStrings: [canonicalRootString],
    notesToPlace: 1,
    acceptedPositions,
    solution,
    listenFretWindow: fretWindow,
  };

  // Audio: cadence establishing the key → diatonic chord arpeggio → block chord.
  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  for (const note of [chordRoot, chordThird, chordFifth]) {
    builder.addNote(note, 0.55, 0.2, "melody");
    builder.advance(0.3);
  }
  builder.advance(0.4);
  builder.addChord([chordRoot, chordThird, chordFifth], 1.4, 0.13, "harmony");
  builder.advance(1.5);
  const audio = builder.build();

  const chordPcName = PC_NAMES[((diatonicRootMidi % 12) + 12) % 12]!;
  const qualityLabel = quality === "maj" ? "major" : "minor";

  return {
    itemId,
    exerciseType: "fret-diatonic-recognition",
    audio,
    prompt: `Tap the ${params.degree} root`,
    options: [],
    correctAnswerId: "",
    meta: {
      keyName: key.name,
      seed,
      explanation: `${chordPcName} ${qualityLabel} — ${params.shape}-shape, degree ${params.degree} in ${key.name}.`,
      distractorCount: 0,
      tonicMidi: diatonicRootMidi, // chord root used for relative solfège on dots
      tonalityMode: quality === "maj" ? "major" : "minor",
      teachAudio: buildDiatonicTeachPlan(chordRoot, chordThird, chordFifth),
      fretboard,
      playbackMidis: [chordRoot, chordThird, chordFifth],
    },
  };
}

function buildDiatonicTeachPlan(rootMidi: number, thirdMidi: number, fifthMidi: number): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const note of [rootMidi, thirdMidi, fifthMidi]) {
    builder.addNote(note, 0.8, 0.22, "melody");
    builder.advance(0.4);
  }
  builder.advance(0.3);
  builder.addChord([rootMidi, thirdMidi, fifthMidi], 1.5, 0.13, "harmony");
  builder.advance(1.6);
  return builder.build();
}
